import '../../i18n/autoTranslate';
import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import SpotlightCard from '../../../components/ui/SpotlightCard';

const RealTimeFeedback = ({ 
  isVisible = true,
  sessionData = null
}) => {
  const [feedbackData, setFeedbackData] = useState({
    confidenceLevel: 75,
    speakingPace: 'optimal',
    fillerWords: 3,
    eyeContact: 85,
    posture: 'good',
    clarity: 80,
    volume: 'appropriate'
  });

  const [isMinimized, setIsMinimized] = useState(false);

  useEffect(() => {
    // Simulate real-time feedback updates
    const interval = setInterval(() => {
      setFeedbackData(prev => ({
        ...prev,
        confidenceLevel: Math.max(0, Math.min(100, prev?.confidenceLevel + (Math.random() - 0.5) * 10)),
        fillerWords: Math.max(0, prev?.fillerWords + (Math.random() > 0.8 ? 1 : 0)),
        eyeContact: Math.max(0, Math.min(100, prev?.eyeContact + (Math.random() - 0.5) * 8)),
        clarity: Math.max(0, Math.min(100, prev?.clarity + (Math.random() - 0.5) * 6))
      }));
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  if (!isVisible) return null;

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-400';
    if (score >= 60) return 'text-indigo-400';
    return 'text-yellow-400';
  };

  const getPaceColor = (pace) => {
    switch (pace) {
      case 'optimal': return 'text-green-400';
      case 'fast': return 'text-yellow-400';
      case 'slow': return 'text-yellow-400';
      default: return 'text-gray-400';
    }
  };

  const feedbackItems = [
    {
      label: 'Confidence',
      value: feedbackData?.confidenceLevel,
      type: 'percentage',
      icon: 'TrendingUp',
      description: 'Voice tone and body language'
    },
    {
      label: 'Eye Contact',
      value: feedbackData?.eyeContact,
      type: 'percentage',
      icon: 'Eye',
      description: 'Camera engagement'
    },
    {
      label: 'Clarity',
      value: feedbackData?.clarity,
      type: 'percentage',
      icon: 'Volume2',
      description: 'Speech articulation'
    },
    {
      label: 'Speaking Pace',
      value: feedbackData?.speakingPace,
      type: 'text',
      icon: 'Clock',
      description: 'Words per minute'
    },
    {
      label: 'Filler Words',
      value: feedbackData?.fillerWords,
      type: 'count',
      icon: 'AlertTriangle',
      description: 'Um, uh, like, you know'
    }
  ];

  return (
    <div className={`fixed right-1.5 top-48 z-40 transition-all duration-300 ease-smooth ${
      isMinimized ? 'w-24' : 'w-1/2'
    } hidden lg:block`}>
      <SpotlightCard spotlightColor="rgba(139, 92, 246, 0.15)">
        <div className="bg-gradient-to-br from-[rgba(30,30,46,0.95)] via-[rgba(30,30,46,0.9)] to-[rgba(46,46,69,0.6)] border border-white/10 rounded-lg overflow-hidden backdrop-blur">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-700">
            {!isMinimized && (
              <div className="flex items-center space-x-2">
                <Icon name="Activity" size={20} className="text-indigo-400" />
                <h3 className="font-semibold text-white">Live Feedback</h3>
              </div>
            )}
            
            <button
              onClick={() => setIsMinimized(!isMinimized)}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/10 text-gray-400 hover:text-white transition-colors duration-200"
            >
              <Icon name={isMinimized ? "ChevronLeft" : "ChevronRight"} size={16} />
            </button>
          </div>

          {/* Feedback Content */}
          {!isMinimized && (
            <div className="p-4 space-y-4">
              {/* Overall Score */}
              <div className="text-center p-4 bg-gradient-to-r from-purple-500/10 to-indigo-500/10 rounded-lg border border-purple-500/20">
                <div className="text-2xl font-bold text-white mb-1">
                  {(
                    (feedbackData?.confidenceLevel +
                      feedbackData?.eyeContact +
                      feedbackData?.clarity) /
                    3
                  ).toFixed(0)}%
                </div>
                <div className="text-sm text-gray-400">Overall Performance</div>
              </div>
          
              {/* Detailed Metrics in 2-column grid */}
              <div className="grid grid-cols-2 gap-3">
                {feedbackItems?.map((item, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-800/30 border border-gray-700/50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <Icon name={item?.icon} size={16} className="text-gray-400" />
                      <div>
                        <div className="text-sm font-medium text-white">{item?.label}</div>
                        <div className="text-xs text-gray-400">{item?.description}</div>
                      </div>
                    </div>
          
                    <div className="text-right">
                      {item?.type === 'percentage' && (
                        <div className={`text-sm font-semibold ${getScoreColor(item?.value)}`}>
                          {Math.round(item?.value)}%
                        </div>
                      )}
                      {item?.type === 'text' && (
                        <div className={`text-sm font-semibold capitalize ${getPaceColor(item?.value)}`}>
                          {item?.value}
                        </div>
                      )}
                      {item?.type === 'count' && (
                        <div className={`text-sm font-semibold ${item?.value > 5 ? 'text-yellow-400' : 'text-green-400'}`}>
                          {Math.round(item?.value)}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
          
              {/* Quick Tips */}
              <div className="bg-indigo-500/10 border border-indigo-500/20 rounded-lg p-3">
                <div className="flex items-start space-x-2">
                  <Icon name="Lightbulb" size={16} className="text-indigo-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="text-sm font-medium text-indigo-400 mb-1">Quick Tip</div>
                    <div className="text-xs text-gray-300">
                      {feedbackData?.fillerWords > 5
                        ? "Try pausing instead of using filler words. Take a breath before continuing."
                        : feedbackData?.confidenceLevel < 60
                        ? "Sit up straight and make eye contact with the camera to appear more confident."
                        : "Great job! Keep maintaining this level of engagement."
                      }
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Minimized State */}
          {isMinimized && (
            <div className="p-2 flex flex-col items-center space-y-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-r from-purple-500 to-indigo-600 flex items-center justify-center">
                <span className="text-xs font-bold text-white">
                  {Math.round((feedbackData?.confidenceLevel + feedbackData?.eyeContact + feedbackData?.clarity) / 3)}
                </span>
              </div>
              <div className="w-1 h-4 bg-gradient-to-b from-green-400 to-indigo-400 rounded-full"></div>
            </div>
          )}
        </div>
      </SpotlightCard>
    </div>
  );
};

export default RealTimeFeedback;
